import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import {
  ShieldCheck, Mail, IdCard, CheckCircle2, AlertTriangle,
  Loader2, Sparkles, Camera, Clock, X as XIcon,
} from "lucide-react";

/**
 * OrganizerVerify
 * ---------------
 * 3-step verification wizard (post-Twilio-removal, Feb 2026):
 *
 *   1. Real legal name (typed in)
 *   2. Email — signed 24-hour token, delivered by Resend
 *   3. Manual verification — organizer submits phone + optional in-app
 *      ID + venue photos (or emails them to admin), admin flips the
 *      kyc_approved flag after review.
 *
 * Once step 3 is approved by an admin the header verified-organizer
 * badge upgrades to "ID-verified" and Stripe Connect payouts unlock.
 */

/* ---------- Small shared UI ---------- */

function StepCard({ testId, done, active, blocked, title, subtitle, icon: Icon, accent, children }) {
  const borderColor = done ? "rgba(132,204,22,0.5)"
    : active ? "var(--avt-purple)"
    : blocked ? "rgba(0,0,0,0.08)"
    : "rgba(0,0,0,0.1)";
  const bg = done ? "rgba(132,204,22,0.06)" : "#fff";
  return (
    <div data-testid={testId}
         data-verify-done={done ? "true" : "false"}
         className="rounded-2xl border-2 p-5 sm:p-6 transition"
         style={{
           borderColor,
           background: bg,
           opacity: blocked ? 0.5 : 1,
         }}>
      <div className="flex items-start gap-4">
        <span className="rounded-full p-3 flex-shrink-0"
              style={{ backgroundColor: `${accent}18`, color: accent }}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-xl">{title}</h3>
            {done && (
              <span data-testid={`${testId}-done-badge`}
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ backgroundColor: "rgba(132,204,22,0.15)", color: "#4B7A0A" }}>
                <CheckCircle2 className="h-3 w-3" /> Done
              </span>
            )}
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>{subtitle}</p>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function ProviderMissingNotice({ text }) {
  return (
    <div className="text-xs rounded-md px-3 py-2 inline-flex items-center gap-2"
         style={{ backgroundColor: "rgba(249,115,22,0.10)", color: "var(--avt-orange)" }}>
      <AlertTriangle className="h-3 w-3" /> {text}
    </div>
  );
}

/* ---------- Page ---------- */

export default function OrganizerVerify() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [manualIdPhoto, setManualIdPhoto] = useState("");
  const [manualVenuePhoto, setManualVenuePhoto] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [searchParams] = useSearchParams();

  const reload = async () => {
    try {
      const [{ data }, { data: m }] = await Promise.all([
        api.get("/organizer/verify/status"),
        api.get("/organizer/verify/manual-status"),
      ]);
      setStatus(data);
      setManual(m);
      setFirstName(data.name.legal_first_name || "");
      setLastName(data.name.legal_last_name || "");
      // Hydrate manual form from last submission so organizer can revise.
      if (m.manual_phone) setManualPhone(m.manual_phone);
      if (m.manual_id_photo_url) setManualIdPhoto(m.manual_id_photo_url);
      if (m.manual_venue_photo_url) setManualVenuePhoto(m.manual_venue_photo_url);
      if (m.manual_notes) setManualNotes(m.manual_notes);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load status");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setB = (k, v) => setBusy((s) => ({ ...s, [k]: v }));

  const saveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Enter both first and last legal name.");
      return;
    }
    setB("name", true);
    try {
      await api.post("/organizer/verify/name", {
        legal_first_name: firstName.trim(),
        legal_last_name: lastName.trim(),
      });
      toast.success("Legal name saved");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setB("name", false); }
  };

  const sendEmail = async () => {
    setB("email", true);
    try {
      await api.post("/organizer/verify/email/send");
      toast.success("Check your inbox — link expires in 24 hours");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email");
    } finally { setB("email", false); }
  };

  const submitManual = async () => {
    if (!manualPhone.trim()) {
      toast.error("Add your real phone number so we can reach you.");
      return;
    }
    setB("manual", true);
    try {
      await api.post("/organizer/verify/manual-submit", {
        phone: manualPhone.trim(),
        id_photo_url: manualIdPhoto || null,
        venue_photo_url: manualVenuePhoto || null,
        notes: manualNotes.trim() || null,
      });
      toast.success("Sent! We'll review and email you within 1-2 business days.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    } finally { setB("manual", false); }
  };

  if (!user || user.role !== "organizer") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-12 text-center">
        <p style={{ color: "var(--ink-soft)" }}>
          Organizer accounts only. <button className="underline" onClick={() => navigate("/")}>Go home</button>
        </p>
      </div>
    );
  }
  if (loading || !status) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
      </div>
    );
  }

  const nameDone = Boolean(status.name.legal_first_name && status.name.legal_last_name);
  const emailDone = status.email.verified;
  const manualStatus = manual?.manual_status || "not_submitted";
  const manualPending = manualStatus === "pending";
  const manualRejected = manualStatus === "rejected";
  const manualDone = Boolean(manual?.kyc_approved);
  const allDone = nameDone && emailDone && manualDone;
  const doneCount = [nameDone, emailDone, manualDone].filter(Boolean).length;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 py-10" data-testid="organizer-verify">
      {/* Hero */}
      <div className="flex items-center gap-3">
        <span className="rounded-full p-3"
              style={{ backgroundColor: "rgba(107,47,186,0.14)", color: "var(--avt-purple)" }}>
          <ShieldCheck className="h-6 w-6" />
        </span>
        <div>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>Organizer Verification</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            {allDone ? <>You&rsquo;re <span style={{ color: "#4B7A0A" }}>fully verified.</span></>
              : <>Prove you&rsquo;re real.</>}
          </h1>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Verified organizers unlock the ID-verified badge, priority placement in event
        discovery, and can accept Stripe payouts. Three quick steps &mdash; do them
        whenever it&rsquo;s convenient, in any order.
      </p>

      {/* Progress bar */}
      <div className="mt-6 rounded-full h-2 overflow-hidden"
           style={{ backgroundColor: "rgba(107,47,186,0.12)" }}
           data-testid="verify-progress">
        <div className="h-full transition-all"
             data-testid="verify-progress-fill"
             style={{
               width: `${(doneCount / 3) * 100}%`,
               background: allDone
                 ? "linear-gradient(90deg, #84CC16, #22c55e)"
                 : "linear-gradient(90deg, #6B2FBA, #F97316)",
             }} />
      </div>
      <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
        {doneCount} of 3 complete
      </div>

      {allDone && (
        <div className="mt-6 rounded-xl p-4 border-2"
             data-testid="verify-fully-verified"
             style={{ borderColor: "rgba(132,204,22,0.5)", backgroundColor: "rgba(132,204,22,0.08)" }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: "#4B7A0A" }} />
            <span className="font-serif text-lg" style={{ color: "#4B7A0A" }}>You&rsquo;re fully verified.</span>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
            The ID-verified badge is live on your profile and every event card.
          </p>
        </div>
      )}

      {/* 4 steps */}
      <div className="mt-8 space-y-4">
        {/* --- Legal name --- */}
        <StepCard
          testId="verify-step-name"
          done={nameDone}
          active={!nameDone}
          title="1. Real legal name"
          subtitle="This must match the name on your government ID."
          icon={IdCard}
          accent="var(--avt-purple)"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Legal first name</Label>
              <Input data-testid="verify-legal-first" value={firstName}
                     onChange={(e) => setFirstName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Legal last name</Label>
              <Input data-testid="verify-legal-last" value={lastName}
                     onChange={(e) => setLastName(e.target.value)} className="mt-1" />
            </div>
          </div>
          <Button onClick={saveName} disabled={busy["name"]} data-testid="verify-legal-save"
                  className="btn-primary mt-4 rounded-full px-5 py-2.5">
            {busy["name"] ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save name"}
          </Button>
        </StepCard>

        {/* --- Email --- */}
        <StepCard
          testId="verify-step-email"
          done={emailDone}
          active={nameDone && !emailDone}
          title="2. Confirm your email"
          subtitle={`We'll send a one-time link to ${status.email.value}.`}
          icon={Mail}
          accent="var(--avt-teal)"
        >
          {emailDone ? (
            <div className="text-sm" style={{ color: "#4B7A0A" }}>
              Verified{status.email.verified_at ? ` on ${new Date(status.email.verified_at).toLocaleDateString()}` : ""}.
            </div>
          ) : (
            <Button onClick={sendEmail} disabled={busy["email"]} data-testid="verify-email-send"
                    className="btn-primary rounded-full px-5 py-2.5">
              {busy["email"] ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send confirmation link"}
            </Button>
          )}
        </StepCard>

        {/* --- Manual verification (replaces phone OTP + Stripe Identity) --- */}
        <StepCard
          testId="verify-step-manual"
          done={manualDone}
          active={emailDone && !manualDone}
          title="3. Verify with our admin team"
          subtitle={manualPending
            ? "We got your submission — reviewing now. Usually 1-2 business days."
            : manualRejected
              ? "We couldn't approve last time — check the note below and try again."
              : "Send us a selfie holding your government ID + a photo of your venue info, and your real phone number for our records."}
          icon={ShieldCheck}
          accent="var(--avt-orange)"
        >
          {manualDone ? (
            <div className="text-sm space-y-1" style={{ color: "#4B7A0A" }} data-testid="verify-manual-approved">
              <div className="inline-flex items-center gap-1.5 font-medium">
                <CheckCircle2 size={16} /> Approved
                {manual?.manual_reviewed_at && (
                  <span style={{ color: "var(--ink-soft)" }}>
                    · {new Date(manual.manual_reviewed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div style={{ color: "var(--ink-soft)" }}>
                Phone on file: <strong>{manual.manual_phone || "—"}</strong>
              </div>
            </div>
          ) : (
            <>
              {manualPending && (
                <div
                  data-testid="verify-manual-pending"
                  className="mb-4 flex items-start gap-2 rounded-md p-3 text-sm"
                  style={{ background: "rgba(249,115,22,0.08)", color: "var(--avt-orange)" }}
                >
                  <Clock size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Submitted {manual.manual_submitted_at
                      ? new Date(manual.manual_submitted_at).toLocaleDateString()
                      : "just now"}.</strong>
                    &nbsp;Our team will email you as soon as you&rsquo;re approved.
                  </div>
                </div>
              )}
              {manualRejected && manual?.manual_reject_reason && (
                <div
                  data-testid="verify-manual-rejected"
                  className="mb-4 rounded-md p-3 text-sm"
                  style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}
                >
                  <div className="font-medium">Needs another look</div>
                  <div className="mt-1" style={{ color: "var(--ink-soft)" }}>
                    {manual.manual_reject_reason}
                  </div>
                </div>
              )}

              <div className="rounded-md p-4 text-sm"
                   style={{ background: "rgba(107,47,186,0.06)", border: "1px solid rgba(107,47,186,0.15)" }}>
                <div className="font-medium mb-1">Send us:</div>
                <ol className="list-decimal ml-5 space-y-0.5" style={{ color: "var(--ink-soft)" }}>
                  <li>A selfie of you holding your government photo ID</li>
                  <li>Photos or scan of your venue info / permits</li>
                  <li>Your real phone number (below)</li>
                </ol>
                <div className="mt-3">
                  Email attachments to&nbsp;
                  <a
                    href={`mailto:${manual?.admin_email || "admin@addvendtour.com"}?subject=${encodeURIComponent("Organizer verification: " + (user?.email || ""))}`}
                    className="underline font-medium"
                    data-testid="verify-manual-mailto"
                    style={{ color: "var(--avt-purple)" }}
                  >
                    {manual?.admin_email || "admin@addvendtour.com"}
                  </a>
                  &nbsp;— or upload them right here, whichever is easier.
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest">Your real phone number</Label>
                  <Input
                    data-testid="verify-manual-phone"
                    value={manualPhone}
                    onChange={(e) => setManualPhone(e.target.value)}
                    placeholder="+1 (512) 555-1234"
                    autoComplete="tel"
                    className="mt-1"
                  />
                  <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                    Kept privately in AddVendTour records — never shown publicly.
                  </div>
                </div>

                <PhotoField
                  label="Selfie with photo ID (optional in-app upload)"
                  testId="verify-manual-id"
                  url={manualIdPhoto}
                  onChange={setManualIdPhoto}
                  hint="Hold your ID next to your face; make sure both are readable."
                />

                <PhotoField
                  label="Venue photo or permit (optional in-app upload)"
                  testId="verify-manual-venue"
                  url={manualVenuePhoto}
                  onChange={setManualVenuePhoto}
                  hint="A permit, insurance certificate, or a clear photo of the venue you host at."
                />

                <div>
                  <Label className="text-[10px] uppercase tracking-widest">Anything else? (optional)</Label>
                  <Textarea
                    data-testid="verify-manual-notes"
                    value={manualNotes}
                    onChange={(e) => setManualNotes(e.target.value)}
                    maxLength={800}
                    rows={3}
                    placeholder="Which markets you organize, how long you've been at it, anything that helps us verify you faster."
                    className="mt-1"
                  />
                </div>

                <Button
                  onClick={submitManual}
                  disabled={busy["manual"] || !manualPhone.trim()}
                  data-testid="verify-manual-submit"
                  className="btn-primary rounded-full px-5 py-2.5"
                >
                  {busy["manual"]
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : manualPending
                      ? "Update my submission"
                      : "Submit for review"}
                </Button>
              </div>
            </>
          )}
        </StepCard>
      </div>
    </div>
  );
}

/** Small photo picker with live preview + remove — used twice on this page. */
function PhotoField({ label, url, onChange, hint, testId }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest">{label}</Label>
      <div className="mt-2 flex items-start gap-3">
        {url ? (
          <div className="relative shrink-0">
            <img
              src={url}
              alt=""
              data-testid={`${testId}-preview`}
              className="w-24 h-24 rounded-md object-cover border"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
            />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testId}-clear`}
              className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"
              style={{ border: "1px solid rgba(0,0,0,0.15)" }}
              aria-label="Remove photo"
            >
              <XIcon size={12} />
            </button>
          </div>
        ) : (
          <div
            className="w-24 h-24 rounded-md border-2 border-dashed flex items-center justify-center shrink-0"
            style={{ borderColor: "rgba(0,0,0,0.12)", color: "var(--ink-soft)" }}
          >
            <Camera size={20} />
          </div>
        )}
        <div className="flex-1">
          <ImageUploader
            onUploaded={onChange}
            label={url ? "Replace" : "Upload"}
            testId={testId}
          />
          {hint && (
            <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>{hint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
